import Image from "next/image";
import Link from "next/link";

import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import type { PopupSlide } from "@/v2-components/ui/floating-popup";
import {
  isDismissedToday,
  usePopupBannerStore,
} from "@/store/popup-banner-store";
import {
  COPY,
  GTA5_GIVEAWAY_POPUP_BG,
  GTA5_GIVEAWAY_PATH,
  GTA5_PRIZE_COUNT,
  GTA5_CLOSE_LABEL_TZ_TIGHT,
} from "@/constants/gta5-giveaway";

/**
 * 홈에 뜨는 GTA V meets TactSuit 기브어웨이 팝업.
 *
 * ## 팝업은 **유입 장치**이고 착지점은 페이지다
 *
 * 팝업만 만들면 기획서가 명시한 두 번째 유입 경로 — *"인플루언서 등 외부 채널에서 QR 및
 * 링크 통해 이벤트 페이지 접속"* — 이 갈 곳이 없다. 홈으로 보내도 「오늘 하루 안 보기」를
 * 누른 사람에겐 안 뜬다. 그래서 CTA 는 구글폼이 아니라 **랜딩 페이지**로 보낸다.
 * 폼으로 내보내는 것은 그 페이지가 한다 — 깔때기를 한 줄기로 둔다.
 *
 * ## ★노출 판정은 **여기서 하지 않는다** — 홈이 서버에서 정해 내려 준다
 *
 * 종전엔 `useIsFeatureOn(Gta5GiveawayEnabled)` 을 여기서 불렀다. 클라이언트 판정은
 * 로딩 중 `null` 이라 깜빡임을 신경 써야 했고, 무엇보다 **랜딩 페이지의 서버 판정과
 * 갈릴 수 있었다** — 그러면 팝업은 떴는데 눌러 보니 404 다.
 *
 * 지금은 `pages/index.tsx` 의 `getServerSideProps` 가 `isGta5GiveawayOpen` 을 불러
 * `open` 으로 내려 준다. 랜딩과 **같은 함수·같은 층**이다.
 */

/**
 * ## ★**팝업이 아니라 «슬라이드 제공자»** 가 됐다 (2026-09-11)
 *
 * 종전엔 이 컴포넌트가 자기 `FloatingPopup` 을 그렸다. 그런데 웰컴 쿠폰 팝업이
 * **같은 자리·같은 `z-50`** 에 따로 떠서 겹쳤다(실측은 `home-floating-popups.tsx`).
 * 껍데기는 **홈에 하나**여야 하므로 그 조합기가 그리고, 여기서는 **이 캠페인의
 * 슬라이드만** 돌려준다.
 *
 * 그래서 이 캠페인의 것은 다 여기 남는다 — 저장소(`usePopupBannerStore`) ·
 * 카드 마크업. 조합기는 순서만 정한다.
 */
export function useGta5PopupSlides({ open }: { open: boolean }): PopupSlide[] {
  const { t } = useTrans(["event"]);

  /**
   * 「오늘 하루 안 보기」는 **이 캠페인 저장소**를 쓴다 (작성자 확정 2026-09-10:
   * *"해당 장면의 이벤트만 영향"*). 껍데기는 슬라이드마다 이 콜백을 부른다 — 웰컴 쿠폰이
   * 같이 뜨는 날에도 서로를 끄지 않는다.
   */
  const dismissedUntil = usePopupBannerStore((s) => s.dismissedUntil);
  const dismiss = usePopupBannerStore((s) => s.dismiss);

  /**
   * ⚠️ `isDismissedToday` 는 localStorage 를 읽어 서버가 모른다. 그래도 여기서 판정할 수
   * 있는 것은 껍데기가 **마운트 뒤에만** 그리기 때문이다(`useSyncExternalStore`).
   */
  /**
   * ★**데모 쿼리(`?popup-demo=1`)는 걷혔다** (작성자 요청 2026-09-11).
   *
   * 그 쿼리는 prod 에서도 돌았고(staging 이 `NEXT_PUBLIC_ENV=production`), 진짜
   * 캠페인 슬라이드까지 열지 않도록 데모를 **분리**해 두는 구조가 필요했다
   * (리뷰 #5726 R1 b1). 검수 목적이 끝나 쿼리를 지우면서 그 분리도 같이 사라졌다 —
   * 지금은 `open` 하나가 유일한 조건이다. 되살리려면 그 리뷰부터 다시 읽을 것.
   */
  const realSlides =
    open && !isDismissedToday(dismissedUntil)
      ? [
          {
            key: "gta5-giveaway",
            label: t("popupTitle", { prizeCount: GTA5_PRIZE_COUNT }),
            onDismissToday: dismiss,
            node: <Gta5Slide />,
          },
        ]
      : [];

  return realSlides;
}

function Gta5Slide() {
  const { t } = useTrans(["event"]);

  return (
    /**
     * ★**배경 위에 라이브 텍스트** — 시안 `30835:157629` (`front-card` 의 `image`,
     * 2026-09-11).
     *
     * 종전엔 **문구가 구워진 이미지 한 장**이었고 그 자리 주석이 *"최종 배경판이
     * 오면 제목·부제를 DOM 으로 올린다"* 였다. 그 배경판이 왔다 —
     * `GTA5_GIVEAWAY_POPUP_BG`(720×380, 글자 없음, 왼쪽이 어둡게 처리됨).
     *
     * ```
     *   슬롯      360×190 · p16 · items-center
     *   Contents  w180 · gap16 · justify-center
     *     eyebrow  12/16 .4        white
     *     title    14/18 SemiBold .25  white
     *     버튼      h40 r44 px24 · bg Blue/60 · 12/16 SemiBold .1 white
     *     각주      10/14 .4        white
     * ```
     *
     * ★**폭 180 을 고정한다** — 배경 오른쪽에 인물이 있어 글자가 거기까지 가면
     * 안 읽힌다. 시안이 `w-[180px]` 로 명시한다.
     *
     * ⚠️ 이미지에 `priority` 를 준다 — 팝업은 첫 화면에 뜨고 이 배경이 카드의
     * 전부다. 히어로와 달리 **판이 하나**라 경쟁할 형제가 없다.
     */
    <div className="relative flex h-[190px] items-center bg-black p-4">
      <Image
        src={GTA5_GIVEAWAY_POPUP_BG}
        alt=""
        fill
        sizes="360px"
        className="object-cover"
        priority
      />
      <div className="relative flex w-[180px] flex-col justify-center gap-4">
        <div className="flex flex-col gap-1 text-white">
          <p className="text-sm font-400 leading-4 tracking-[0.4px]">
            {t("popupEyebrow")}
          </p>
          <p className="text-base font-600 leading-[18px] tracking-[0.25px]">
            {t("popupBanner", { closeLabelTzTight: GTA5_CLOSE_LABEL_TZ_TIGHT })}
          </p>
        </div>
        <Link
          href={GTA5_GIVEAWAY_PATH}
          className="flex h-10 w-fit items-center justify-center rounded-[44px] bg-blue-60 px-6 text-sm font-600 leading-4 tracking-[0.1px] text-white transition-colors hover:bg-blue-70"
        >
          {t("cta")}
        </Link>
        <p className="text-2xs font-400 leading-[14px] tracking-[0.4px] text-white">
          {t("popupFootnote")}
        </p>
      </div>
    </div>
  );
}
